import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import DriverInfoCard from '../components/DriverInfoCard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Edit, Trash2, Search, MapPin, CheckCircle, Star } from 'lucide-react';
import { format } from 'date-fns';
import RateDriverModal from '../components/RateDriverModal';

const RideList = () => {
  const { user } = useContext(AuthContext);
  const [rides, setRides] = useState([]);
  const [filteredRides, setFilteredRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [expandedRide, setExpandedRide] = useState(null);
  const [ratingModal, setRatingModal] = useState({ open: false, driverId: null, rideId: null, driverName: '' });
  const navigate = useNavigate();
  const isRider = user?.role === 'rider';

  useEffect(() => {
    fetchRides();
  }, []);

  useEffect(() => {
    if (search) {
      const filtered = rides.filter(ride =>
        ride.client_name.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredRides(filtered);
    } else {
      setFilteredRides(rides);
    }
  }, [search, rides]);

  const fetchRides = async () => {
    try {
      const response = await axios.get(`${API}/rides`);
      setRides(response.data);
      setFilteredRides(response.data);
    } catch (error) {
      toast.error('Failed to load rides');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/rides/${deleteId}`);
      toast.success('Ride deleted successfully');
      setRides(rides.filter(ride => ride.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      toast.error('Failed to delete ride');
    }
  };

  const handlePickup = async (rideId) => {
    try {
      await axios.post(`${API}/rides/${rideId}/pickup`);
      toast.success('Rider picked up! Ready for dropoff.');
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as picked up');
    }
  };

  const handleDropoff = async (rideId) => {
    try {
      await axios.post(`${API}/rides/${rideId}/dropoff`);
      toast.success('Ride completed! Great job!');
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete ride');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'available': { color: 'bg-blue-100 text-blue-800', label: 'Available' },
      'claimed': { color: 'bg-gold-100 text-gold-800', label: 'Claimed' },
      'assigned': { color: 'bg-purple-100 text-purple-800', label: 'Assigned' },
      'picked_up': { color: 'bg-orange-100 text-orange-800', label: 'In Progress' },
      'completed': { color: 'bg-green-100 text-green-800', label: 'Completed' },
      'requested': { color: 'bg-indigo-100 text-indigo-800', label: 'Requested' },
      'cancelled': { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
    };
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const isAssignedToMe = (ride) => {
    return ride.assigned_to === user.id;
  };

  const isCreatedByMe = (ride) => {
    return ride.user_id === user.id;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading rides...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ride-list-page">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <CardTitle className="text-2xl">{isRider ? 'My Ride Requests' : 'All Rides'}</CardTitle>
            {!isRider && (
              <Button onClick={() => navigate('/rides/new')} className="btn-primary" data-testid="add-new-ride-button">
                Add New Ride
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!isRider && (
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by client name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 form-input"
                  data-testid="search-rides-input"
                />
              </div>
            </div>
          )}

          {filteredRides.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">No rides found</p>
              {search && (
                <Button variant="outline" onClick={() => setSearch('')} data-testid="clear-search-button">
                  Clear Search
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Status</TableHead>
                    {!isRider && <TableHead>Client</TableHead>}
                    {!isRider && <TableHead>Phone</TableHead>}
                    {!isRider && <TableHead className="text-right">Miles</TableHead>}
                    <TableHead className="text-right">Total</TableHead>
                    {!isRider && <TableHead className="text-right">Commission</TableHead>}
                    {!isRider && <TableHead className="text-right">Net</TableHead>}
                    {!isRider && <TableHead className="text-right">Actions</TableHead>}
                    {isRider && <TableHead>Driver</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRides.map((ride) => (
                    <React.Fragment key={ride.id}>
                      <TableRow className="table-row" data-testid={`ride-row-${ride.id}`}>
                        <TableCell className="font-medium">
                          {format(new Date(ride.date), 'MMM dd, yyyy HH:mm')}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {ride.pickup_location} → {ride.dropoff_location}
                        </TableCell>
                        <TableCell>{getStatusBadge(ride.status)}</TableCell>
                        {!isRider && (
                          <TableCell>
                            <div className="flex flex-col">
                              <span>{ride.client_name}</span>
                              {isAssignedToMe(ride) && !isCreatedByMe(ride) && (
                                <span className="text-xs text-blue-600 font-medium">Assigned to me</span>
                              )}
                              {isCreatedByMe(ride) && !isAssignedToMe(ride) && (
                                <span className="text-xs text-gray-500">Created by me</span>
                              )}
                            </div>
                          </TableCell>
                        )}
                        {!isRider && <TableCell className="text-sm text-gray-600">{ride.client_phone}</TableCell>}
                        {!isRider && <TableCell className="text-right">{ride.miles_driven}</TableCell>}
                        <TableCell className="text-right">${ride.total_amount.toFixed(2)}</TableCell>
                        {!isRider && (
                          <TableCell className="text-right text-orange-600">
                            ${ride.commission_amount.toFixed(2)}
                          </TableCell>
                        )}
                        {!isRider && (
                          <TableCell className="text-right font-semibold text-green-600">
                            ${ride.net_earnings.toFixed(2)}
                          </TableCell>
                        )}
                        {isRider && ride.assigned_to && (
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setExpandedRide(expandedRide === ride.id ? null : ride.id)}
                              data-testid={`view-driver-${ride.id}`}
                            >
                              {expandedRide === ride.id ? 'Hide' : 'View'} Driver
                            </Button>
                          </TableCell>
                        )}
                        {isRider && !ride.assigned_to && (
                          <TableCell>
                            <span className="text-gray-500 text-sm">Pending assignment</span>
                          </TableCell>
                        )}
                        {!isRider && (
                          <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {isRider && ride.assigned_to && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setExpandedRide(expandedRide === ride.id ? null : ride.id)}
                                  data-testid={`view-driver-${ride.id}`}
                                >
                                  {expandedRide === ride.id ? 'Hide' : 'View'} Driver
                                </Button>
                                {ride.status === 'completed' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={async () => {
                                      const driverResponse = await axios.get(`${API}/profile/driver/${ride.assigned_to}`);
                                      setRatingModal({
                                        open: true,
                                        driverId: ride.assigned_to,
                                        rideId: ride.id,
                                        driverName: driverResponse.data.name
                                      });
                                    }}
                                    className="bg-gold-50 hover:bg-gold-100 text-gold-700"
                                    data-testid={`rate-driver-${ride.id}`}
                                  >
                                    <Star className="w-4 h-4 mr-1" />
                                    Rate Driver
                                  </Button>
                                )}
                              </>
                            )}
                            {!isRider && (
                              <>
                                {/* Show Pickup button if ride is claimed/assigned */}
                                {(ride.status === 'claimed' || ride.status === 'assigned' || ride.status === 'requested') && ride.assigned_to === user.id && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handlePickup(ride.id)}
                                    className="bg-orange-50 hover:bg-orange-100 text-orange-700"
                                    data-testid={`pickup-ride-${ride.id}`}
                                  >
                                    <MapPin className="w-4 h-4 mr-1" />
                                    Pick Up
                                  </Button>
                                )}
                                {/* Show Dropoff button if ride is picked up */}
                                {ride.status === 'picked_up' && ride.assigned_to === user.id && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDropoff(ride.id)}
                                    className="bg-green-50 hover:bg-green-100 text-green-700"
                                    data-testid={`dropoff-ride-${ride.id}`}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Drop Off
                                  </Button>
                                )}
                                {/* Show edit/delete only for non-active rides or own rides */}
                                {(ride.status === 'completed' || ride.status === 'cancelled' || ride.user_id === user.id) && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => navigate(`/rides/edit/${ride.id}`)}
                                      data-testid={`edit-ride-${ride.id}`}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => setDeleteId(ride.id)}
                                      data-testid={`delete-ride-${ride.id}`}
                                    >
                                      <Trash2 className="w-4 h-4 text-red-600" />
                                    </Button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                        )}
                      </TableRow>
                      {expandedRide === ride.id && ride.assigned_to && (
                        <TableRow>
                          <TableCell colSpan={isRider ? 5 : 10} className="bg-gray-50">
                            <DriverInfoCard driverId={ride.assigned_to} />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <RateDriverModal
        open={ratingModal.open}
        onClose={() => setRatingModal({ open: false, driverId: null, rideId: null, driverName: '' })}
        driverId={ratingModal.driverId}
        rideId={ratingModal.rideId}
        driverName={ratingModal.driverName}
        onRatingSubmitted={() => fetchRides()}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ride</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this ride? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RideList;
